import React from 'react';
import { VerticalContentsHandler } from 'component/page/detail/DetailPageModularize/ContentsHandler/VerticalContentsHandler';
import { BackgroundColor, DisplaySectionName } from 'component/page/detail/DetailPageModularize/detailPageModularize.types';
import { Box, Container, Stack } from '@drone-js/core';
import { FloatingPanelContext, FloatingPanelProvider } from 'component/page/detail/FloatingSidePanel/FloatingPanelContext';
import { useSelector } from 'react-redux';
import { selectScreenSize } from 'store/slice/screen';
import { selectInfoOverlayVisible, selectIsAnyOverlayVisible } from 'store/slice/overlay';
import { DetailPageDisplayConfig } from 'component/page/detail/DetailPageModularize/contentRegistry.type';
import { ContentContainerWithSpacing } from 'component/common/ContentContainer/ContentContainer';
import { ActivitiesToastNotification } from 'component/common/ActToastNotification/ActivitiesToastNotification';
import { CartFloatingContainer } from 'component/common/CartFloatingButtonContainer/CartFloatingButtonContainer';
import FloatingSidePanel from 'component/page/detail/FloatingSidePanel/FloatingSidePanel';
import FooterComponent from 'component/page/detail/DetailPageModularize/ContentsHandler/FooterComponent';

interface Props {
    config: DetailPageDisplayConfig[DisplaySectionName.LandingPageMain];
}

const SIDE_PANEL_WIDTH = '351px';

export const LandingPageMainContents: React.FC<Props> = ({ config }) => {
    const screenSize = useSelector(selectScreenSize);
    const { isFloatingPanelVisible } = React.useContext(FloatingPanelContext);
    const isAnyOverlayVisible: boolean = useSelector(selectIsAnyOverlayVisible);
    const isInfoOverlayVisible = useSelector(selectInfoOverlayVisible);
    const { desktopPb, desktopPt, mobilePb, mobilePt, footerVariant } = config.variant;
    const pt = screenSize.desktop ? desktopPt : mobilePt;
    const pb = screenSize.desktop ? desktopPb : mobilePb;

    const isShowFooter = !screenSize.desktop && !isInfoOverlayVisible;
    const footerWrapperRef = React.useRef<HTMLDivElement>(null);
    const [footerHeight, setFooterHeight] = React.useState(0);

    React.useEffect(() => {
        if (footerWrapperRef.current) {
            setFooterHeight(footerWrapperRef.current.offsetHeight);
        }
    }, []);

    // Required to get the height of the footer when it changes dynamically and need an observer for calculate the updated height.
    React.useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === footerWrapperRef.current) {
                    setFooterHeight(entry.contentRect.height);
                }
            }
        });

        const currentFooterWrapper = footerWrapperRef.current;
        if (footerWrapperRef.current) {
            resizeObserver.observe(footerWrapperRef.current);
        }

        return () => {
            if (currentFooterWrapper) {
                resizeObserver.unobserve(currentFooterWrapper);
            }
        };
    }, []);

    return (
        <>
            <Box
                backgroundColor={config.variant.backgroundColor === BackgroundColor.WHITE ? 'base.background.strong' : 'base.background'}
                overflowY={isAnyOverlayVisible ? 'hidden' : undefined}
                aria-hidden={isAnyOverlayVisible}
                data-testid="detail-page-main-contents"
                pt={pt}
                pb={pb}
                flex={1}
            >
                <ContentContainerWithSpacing isNoPt={true}>
                    <div className="DetailContentArea align-center" data-prerender-name="activities-detail-page">
                        <FloatingPanelProvider>
                            <Stack direction="row">
                                <Container flex={1} minWidth={screenSize.desktop ? `calc(100% - ${SIDE_PANEL_WIDTH})` : '100%'}>
                                    <VerticalContentsHandler contents={config.contents} />
                                </Container>
                                {screenSize.desktop && (
                                    <Container width={SIDE_PANEL_WIDTH} position="sticky" top={isAnyOverlayVisible ? '0px' : '16px'} height="100%" minHeight="200px" ms="24">
                                        {isFloatingPanelVisible && <FloatingSidePanel />}
                                    </Container>
                                )}
                            </Stack>
                        </FloatingPanelProvider>
                    </div>
                    <Box
                        position="sticky"
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                        bottom={screenSize.mobile || (screenSize.tablet && !screenSize.desktop) ? `${footerHeight}px` : 0}
                        zIndex={4}
                    >
                        <ActivitiesToastNotification />
                    </Box>
                </ContentContainerWithSpacing>
            </Box>
            <Box ref={footerWrapperRef} data-test-id="FooterWrapper" position="sticky" display="flex" flexDirection="column" bottom="0" zIndex={4}>
                <CartFloatingContainer position="static" />
                {isShowFooter && <FooterComponent variant={footerVariant} />}
            </Box>
        </>
    );
};

LandingPageMainContents.displayName = 'LandingPageMainContents';
